import {Typography, Stack, Slider} from '@mui/material';
import {useState, useContext, useEffect} from 'react';
import APIServiceFormContext from '../../context/APIServiceFormContext';
import {consistentSpacing} from './variables';

/**
 * ScaleQuestionComponent
 * Fully prepared question that requires using a slider, you can use numbers for each of the values or custom values that will display some additional text at the botton of the component. If you are using numbers only, each integer between the range will be an accepted value. State is not being managed directly in this component, it should be done from the parent component. See this directory's README.md for more details.
 *
 * @param {Object} props - Component props
 * @param {string} props.title
 * @param {string?} props.subtitle
 * @param {Number} props.minimum
 * @param {Number} props.maximum - should be greater than props.minimum
 * @param {Array.<{
 *   value: any
 *   cornerLabel: string|null // Value that will appear at one of the ends, this should only be assigned to the first or last element
 *   description: string // text that will appear at the button of the document when that value is currently selected
 * }>} props.marks - Custom options for the slider questions, are optional only used when you want a slider of options between non numerical values
 *
 * @returns {React.Component}
 */
export const ScaleQuestionComponent = ({title, subtitle, minimum, maximum, marks}) => {
  const [value, setValue] = useState(null);
  const [idx, setIdx] = useState(0);
  const {setValidationFunc, setSendDataArgs, savedValue} = useContext(APIServiceFormContext);

  let defaultMarks = [];
  for (let i = 0; i <= maximum - minimum; i++) {
    defaultMarks[i] = {
      value: minimum + i,
      label: `${minimum + i}`,
    };
  }

  const componentValidationWrapper = () => {
    function componentValidation() {
      if (value === null) return 'Please select/click an option.';
      return '';
    }

    return componentValidation;
  };

  useEffect(() => {
    if (typeof savedValue === 'number') {
      setValue(savedValue === undefined ? null : savedValue);
      return;
    }
    if (savedValue === undefined) {
      setValue(marks !== undefined ? marks[0]?.value : idx);
      return;
    }
    if (typeof savedValue !== 'string') return;
    if (marks === undefined) return;
    //second interface(range of non numerical values)
    let theIndex = undefined;

    for (let i = 0; i < marks.length; i++) {
      if (marks[i].value !== savedValue) continue;
      theIndex = i;
      break;
    }
    if (theIndex === undefined) return;
    setValue(savedValue);
    setIdx(theIndex);
  }, [savedValue]);
  useEffect(() => {
    setValidationFunc(componentValidationWrapper);
    setSendDataArgs({data: value === null ? undefined : value});
  }, [value]);
  useEffect(() => {
    if (savedValue === undefined) setValue(marks !== undefined ? marks[0]?.value : idx);
    if (typeof savedValue === 'number') {
      setValue(savedValue === undefined ? null : savedValue);
      return;
    }
    if (typeof savedValue !== 'string') return;
    if (marks === undefined) return;
    //second interface(range of non numerical values)
    let theIndex = undefined;

    for (let i = 0; i < marks.length; i++) {
      if (marks[i].value !== savedValue) continue;
      theIndex = i;
      break;
    }
    if (theIndex === undefined) return;
    setValue(savedValue);
    setIdx(theIndex);
  }, [title]);

  return (
    <Stack
      gap={1}
      rowGap={consistentSpacing}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: 'fit-content',
        mx: 'auto',
      }}
    >
      <Typography variant="subtitle0" my={3}>
        {title}
        <Slider
          sx={{
            mt: 2,
          }}
          key={`slider`}
          valueLabelDisplay={marks === undefined ? 'auto' : 'off'}
          aria-label={title}
          value={marks === undefined ? value : idx}
          step={1}
          onChange={(_, newValue) => {
            if (marks === undefined) {
              setValue(newValue);
              return;
            }
            setIdx(newValue);
            setValue(marks[newValue].value);
          }}
          marks={
            marks === undefined
              ? defaultMarks
              : marks.map((m, idx) => {
                  let newMark = {};
                  newMark.value = idx;
                  newMark.label = m.cornerLabel === undefined ? '' : m.cornerLabel;
                  return newMark;
                })
          }
          min={minimum}
          max={maximum}
        />
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          textAlign: 'center',
          display: subtitle === undefined ? 'none' : 'inline',
        }}
      >
        {subtitle}
      </Typography>
      {marks !== undefined && <Typography variant="subtitle0">{marks[idx].description}</Typography>}
    </Stack>
  );
};
